import { useEffect, useState, RefObject } from "react";

/**
 * A hook that observes the size of an element and returns its dimensions.
 * Uses requestAnimationFrame to prevent "ResizeObserver loop limit exceeded" errors.
 * 
 * @param ref - The React ref of the element to observe
 * @returns The contentRect of the observed element, or null if not yet measured
 */
export function useResizeObserver(ref: RefObject<HTMLElement>) {
  const [dimensions, setDimensions] = useState<DOMRectReadOnly | null>(null);

  useEffect(() => {
    const target = ref.current;
    if (!target) return;

    const observer = new ResizeObserver((entries) => {
      if (!entries[0]) return;
      
      const { contentRect } = entries[0];
      
      // Use requestAnimationFrame to defer the state update.
      // This helps avoid the "ResizeObserver loop limit exceeded" error 
      // by ensuring the state change happens in the next frame.
      window.requestAnimationFrame(() => {
        // Double check the ref still exists before updating state
        if (ref.current) {
          setDimensions(contentRect);
        }
      });
    });

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return dimensions;
}
